import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { ChartConfig } from '../../api/aiDashboardApi';
import { BarChart3, TrendingUp, PieChart, Activity, Maximize2, Download } from 'lucide-react';

interface D3AdvancedChartProps {
  config: ChartConfig;
  width?: number;
  height?: number;
  interactive?: boolean;
}

export const D3AdvancedChart: React.FC<D3AdvancedChartProps> = ({ 
  config, 
  width = 600, 
  height = 400,
  interactive = true 
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [selectedData, setSelectedData] = useState<any>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Color schemes
  const colors = d3.schemeCategory10;

  useEffect(() => {
    if (!svgRef.current || !config.data?.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const margin = { top: 40, right: 40, bottom: 60, left: 60 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Tooltip
    const tooltip = d3.select('body').selectAll('.d3-tooltip').data([0]);
    const tooltipEnter = tooltip.enter().append('div')
      .attr('class', 'd3-tooltip')
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('background', 'rgba(0, 0, 0, 0.8)')
      .style('color', 'white')
      .style('padding', '8px 12px')
      .style('border-radius', '6px')
      .style('font-size', '12px')
      .style('pointer-events', 'none')
      .style('z-index', '1000');

    const tooltipDiv = tooltip.merge(tooltipEnter as any);

    switch (config.type) {
      case 'bar':
        renderBarChart(g, config.data, innerWidth, innerHeight, tooltipDiv);
        break;
      case 'line':
        renderLineChart(g, config.data, innerWidth, innerHeight, tooltipDiv);
        break;
      case 'pie':
        renderPieChart(g, config.data, Math.min(innerWidth, innerHeight) / 2, tooltipDiv);
        break;
      default:
        renderBarChart(g, config.data, innerWidth, innerHeight, tooltipDiv);
    }

    // Add title
    svg.append('text')
      .attr('x', width / 2)
      .attr('y', 25)
      .attr('text-anchor', 'middle')
      .style('font-size', '16px')
      .style('font-weight', 'bold')
      .style('fill', '#374151')
      .text(config.title);

  }, [config, width, height]);

  const renderBarChart = (g: any, data: any[], width: number, height: number, tooltip: any) => {
    const xScale = d3.scaleBand()
      .domain(data.map(d => d.label || d.name))
      .range([0, width])
      .padding(0.2);

    const yScale = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.value || d.count) as number])
      .range([height, 0]);

    // Add axes
    g.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale));

    g.append('g')
      .call(d3.axisLeft(yScale));

    // Add bars
    g.selectAll('.bar')
      .data(data)
      .enter().append('rect')
      .attr('class', 'bar')
      .attr('x', (d: any) => xScale(d.label || d.name) as number)
      .attr('width', xScale.bandwidth())
      .attr('y', (d: any) => yScale(d.value || d.count))
      .attr('height', (d: any) => height - yScale(d.value || d.count))
      .attr('fill', (_d: any, i: number) => colors[i % colors.length])
      .style('cursor', interactive ? 'pointer' : 'default')
      .on('mouseover', (_event: any, d: any) => {
        if (interactive) {
          tooltip
            .style('visibility', 'visible')
            .html(`<strong>${d.label || d.name}</strong><br/>Value: ${(d.value || d.count).toLocaleString()}`);
        }
      })
      .on('mousemove', (event: any) => {
        if (interactive) {
          tooltip
            .style('top', (event.pageY - 10) + 'px')
            .style('left', (event.pageX + 10) + 'px');
        }
      })
      .on('mouseout', () => {
        if (interactive) {
          tooltip.style('visibility', 'hidden');
        }
      })
      .on('click', (_event: any, d: any) => {
        if (interactive) {
          setSelectedData(d);
        }
      });
  };

  const renderLineChart = (g: any, data: any[], width: number, height: number, tooltip: any) => {
    const xScale = d3.scalePoint()
      .domain(data.map(d => d.label || d.name))
      .range([0, width]);

    const yScale = d3.scaleLinear()
      .domain(d3.extent(data, d => d.value || d.count) as [number, number])
      .range([height, 0]);

    // Add axes
    g.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale));

    g.append('g')
      .call(d3.axisLeft(yScale));

    // Create line generator
    const line = d3.line<any>()
      .x(d => xScale(d.label || d.name) as number)
      .y(d => yScale(d.value || d.count));

    // Add line path
    g.append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', colors[0])
      .attr('stroke-width', 2)
      .attr('d', line);

    // Add dots
    g.selectAll('.dot')
      .data(data)
      .enter().append('circle')
      .attr('class', 'dot')
      .attr('cx', (d: any) => xScale(d.label || d.name) as number)
      .attr('cy', (d: any) => yScale(d.value || d.count))
      .attr('r', 4)
      .attr('fill', colors[0])
      .style('cursor', interactive ? 'pointer' : 'default')
      .on('mouseover', (_event: any, d: any) => {
        if (interactive) {
          tooltip
            .style('visibility', 'visible')
            .html(`<strong>${d.label || d.name}</strong><br/>Value: ${(d.value || d.count).toLocaleString()}`);
        }
      })
      .on('mousemove', (event: any) => {
        if (interactive) {
          tooltip
            .style('top', (event.pageY - 10) + 'px')
            .style('left', (event.pageX + 10) + 'px');
        }
      })
      .on('mouseout', () => {
        if (interactive) {
          tooltip.style('visibility', 'hidden');
        }
      });
  };

  const renderPieChart = (g: any, data: any[], radius: number, tooltip: any) => {
    const pie = d3.pie<any>()
      .value(d => d.value || d.count);

    const arc = d3.arc<any>()
      .innerRadius(0)
      .outerRadius(radius);

    g.attr('transform', `translate(${radius + 20},${radius + 20})`);

    const arcs = g.selectAll('.arc')
      .data(pie(data))
      .enter().append('g')
      .attr('class', 'arc');

    // Add pie slices
    arcs.append('path')
      .attr('d', arc)
      .attr('fill', (_d: any, i: number) => colors[i % colors.length])
      .style('cursor', interactive ? 'pointer' : 'default')
      .on('mouseover', (_event: any, d: any) => {
        if (interactive) {
          const percentage = ((d.value / d3.sum(data, (item: any) => item.value || item.count)) * 100).toFixed(1);
          tooltip
            .style('visibility', 'visible')
            .html(`<strong>${d.data.label || d.data.name}</strong><br/>Value: ${d.value.toLocaleString()}<br/>Percentage: ${percentage}%`);
        }
      })
      .on('mousemove', (event: any) => {
        if (interactive) {
          tooltip
            .style('top', (event.pageY - 10) + 'px')
            .style('left', (event.pageX + 10) + 'px');
        }
      })
      .on('mouseout', () => {
        if (interactive) {
          tooltip.style('visibility', 'hidden');
        }
      });

    // Add labels
    arcs.append('text')
      .attr('transform', (d: any) => `translate(${arc.centroid(d)})`)
      .attr('dy', '.35em')
      .style('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('fill', 'white')
      .style('font-weight', 'bold')
      .text((d: any) => {
        const percentage = ((d.value / d3.sum(data, (item: any) => item.value || item.count)) * 100);
        return percentage > 5 ? `${percentage.toFixed(0)}%` : '';
      });
  };

  const downloadChart = () => {
    if (!svgRef.current) return;
    
    const svgData = new XMLSerializer().serializeToString(svgRef.current);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    canvas.width = width;
    canvas.height = height;
    
    img.onload = () => {
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `${config.title.replace(/\s+/g, '_')}_chart.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const getChartIcon = () => {
    switch (config.type) {
      case 'bar': return <BarChart3 className="h-5 w-5" />;
      case 'pie': return <PieChart className="h-5 w-5" />;
      case 'line': return <TrendingUp className="h-5 w-5" />;
      case 'area': return <Activity className="h-5 w-5" />;
      default: return <BarChart3 className="h-5 w-5" />;
    }
  };

  if (!config.data || config.data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
        <div className="flex items-center gap-3 mb-4">
          {getChartIcon()}
          <h3 className="text-lg font-semibold text-gray-800">{config.title}</h3>
        </div>
        <div className="flex items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <BarChart3 className="w-8 h-8 text-gray-400" />
            </div>
            <p className="font-medium">No data available</p>
            <p className="text-sm">Try adjusting your query or filters</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 ${isFullscreen ? 'fixed inset-4 z-50' : ''}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
              {getChartIcon()}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{config.title}</h3>
              <p className="text-sm text-gray-500 capitalize">{config.type} chart • D3.js</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={downloadChart}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Download chart"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Toggle fullscreen"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="p-6">
        <svg
          ref={svgRef}
          width={isFullscreen ? window.innerWidth - 200 : width}
          height={isFullscreen ? window.innerHeight - 200 : height}
          className="w-full"
        />
      </div>

      {/* Selected data info */}
      {selectedData && (
        <div className="px-6 pb-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 mb-2">Selected Data Point</h4>
            <p className="text-blue-700">
              <span className="font-medium">{selectedData.label || selectedData.name}:</span> {(selectedData.value || selectedData.count).toLocaleString()}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};